import * as R from 'ramda';
import * as React from 'react';
import { deleteComponentNode, setComponentNode } from '../../shared/componentNodeMap';
import { emitComponent } from '../../shared/eventListener';
import { getComponentId, withPersistReactEvent } from '../../shared/utils/common';
import { EventEmitTypes, events } from '../../shared/utils/events';
import { ComponentEventListenerTypes } from '../../shared/actionHandler/types';
import { generateHandlers } from '../../shared/actionHandler/utils';
import { generateComponentActionParams } from '../../shared/actionParams';

const LONG_PRESS_TIMEOUT = 1000;
const DOUBLE_CLICK_TIMEOUT = 200;
const INTERSECTION_TIMEOUT = 500;

export class ComponentActionHandler extends React.PureComponent {
  intersectionObserver = null;

  handlerParams = {};

  handlers = {};

  containerRef = null;

  longPressTimer = null;

  preventClick = false;

  doubleClickTimer = null;

  intersectionTimer = null;

  constructor(props) {
    super(props);

    this.handlerParams = { global: props.global, meta: props.meta };
    this.updateHandlersWithParams(props.instance.actions);
  }

  componentDidMount() {
    if (!process.env.isMiniprogram) {
      this.initIntersectionObserver();
      this.observeIntersection();
    }

    events.on(EventEmitTypes.COMPONENT_INTERSECTING_CHANGE, this.onComponentIntersectionChange);

    if (this.handlers.onInit) {
      this.handlers.onInit(undefined);
    }

    this.execActionHooks(this.props.instance.actions);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.global !== this.props.global || nextProps.meta !== this.props.meta) {
      this.updateHandlerParams(nextProps.meta, nextProps.global);
    }

    if (nextProps.instance.actions !== this.props.instance.actions) {
      this.updateHandlersWithParams(nextProps.instance.actions);
    }
  }

  componentWillUnmount() {
    if (!process.env.isMiniprogram) {
      this.unobserveIntersection();
    }

    events.cancel(EventEmitTypes.COMPONENT_INTERSECTING_CHANGE, this.onComponentIntersectionChange);

    deleteComponentNode(this.props.instance.key);

    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
    }
  }

  execActionHooks = (actions) => {
    actions.forEach((action) => {
      if (typeof action.action.action === 'function') {
        return;
      }

      const hooks = action.action.action;

      if (hooks.afterHandlerRender) {
        const params = generateComponentActionParams(action, this.props.instance, this.props.global, this.props.meta);
        try {
          hooks.afterHandlerRender(params);
        } catch (e) {
          console.error('Action "afterHandlerRender" hook throw error: ', e);
        }
      }
    });
  };

  initIntersectionObserver = () => {
    this.intersectionObserver = new window.IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        const key = parseInt(target.getAttribute('data-key'), 10);
        events.emit(EventEmitTypes.COMPONENT_INTERSECTING_CHANGE, isIntersecting, key);
      });
    });
  };

  observeIntersection = () => this.intersectionObserver.observe(this.containerRef);

  unobserveIntersection = () => this.intersectionObserver.unobserve(this.containerRef);

  onComponentIntersectionChange = (isIntersecting, key) => {
    if (key !== this.props.instance.key) {
      return;
    }

    if (this.intersectionTimer) {
      clearTimeout(this.intersectionTimer);
      this.intersectionTimer = null;
    }

    const callback = isIntersecting ? this.onComponentEnterView : this.onComponentLeaveView;
    this.intersectionTimer = window.setTimeout(() => {
      callback();
      clearTimeout(this.intersectionTimer);
      this.intersectionTimer = null;
    }, INTERSECTION_TIMEOUT);
  };

  onComponentEnterView = () => {
    if (this.handlers.onEnterView) {
      this.handlers.onEnterView(undefined);
    }
  };

  onComponentLeaveView = () => {
    if (this.handlers.onLeaveView) {
      this.handlers.onLeaveView(undefined);
    }
  };

  onComponentPress = withPersistReactEvent((e) => {
    if (!this.handlers.onLongPress) {
      return;
    }

    this.longPressTimer = window.setTimeout(() => this.handlers.onLongPress(e), LONG_PRESS_TIMEOUT);
  });

  onComponentPressRelease = () => {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
    }
    this.longPressTimer = null;
  };

  onSingleClick = withPersistReactEvent((e) => {
    this.doubleClickTimer = window.setTimeout(() => {
      if (!this.preventClick) {
        try {
          this.handlers.onClick(e);
        } catch (e) {
          console.error('Exec onClick action error:', e);
        }
      }
    }, DOUBLE_CLICK_TIMEOUT);
  });

  onDoubleClick = withPersistReactEvent((e) => {
    if (this.doubleClickTimer) {
      clearTimeout(this.doubleClickTimer);
      this.doubleClickTimer = null;
    }

    this.preventClick = true;
    try {
      this.handlers.onDoubleClick(e);
    } catch (e) {
      console.error('Exec onClick action error:', e);
    }

    setTimeout(() => {
      this.preventClick = false;
    }, DOUBLE_CLICK_TIMEOUT);
  });

  emitComponentEvent = (e, type) => {
    const {
      props: { instance },
      handlerParams: { global, meta },
    } = this;
    return emitComponent(e, type, instance, meta, global);
  };

  withEmitComponentEvent = (type, handler) => (e) => {
    if (e) {
      e.persist();
    }

    this.emitComponentEvent(e, type);
    if (handler) {
      return handler(e, this.handlerParams);
    }
  };

  updateHandlerParams = (meta, global) => {
    this.handlerParams.meta = meta;
    this.handlerParams.global = global;
  };

  updateHandlersWithParams = (actions) => {
    const {
      onInit,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onDoubleClick,
      onLongPress,
      onEnterView,
      onLeaveView,
    } = generateHandlers(actions, this.props.instance);
    const { withEmitComponentEvent, handlers } = this;

    handlers.onInit = withEmitComponentEvent(ComponentEventListenerTypes.INIT, onInit);
    handlers.onClick = withEmitComponentEvent(ComponentEventListenerTypes.CLICK, onClick);
    handlers.onMouseEnter = withEmitComponentEvent(ComponentEventListenerTypes.MOUSE_ENTER, onMouseEnter);
    handlers.onMouseLeave = withEmitComponentEvent(ComponentEventListenerTypes.MOUSE_LEAVE, onMouseLeave);
    handlers.onDoubleClick = withEmitComponentEvent(ComponentEventListenerTypes.DOUBLE_CLICK, onDoubleClick);
    handlers.onLongPress = withEmitComponentEvent(ComponentEventListenerTypes.LONG_PRESS, onLongPress);
    handlers.onEnterView = withEmitComponentEvent(ComponentEventListenerTypes.ENTER_VIEW, onEnterView);
    handlers.onLeaveView = withEmitComponentEvent(ComponentEventListenerTypes.LEAVE_VIEW, onLeaveView);
  };

  setNode = (node) => {
    this.containerRef = node;
    setComponentNode(this.props.instance.key, node);
  };

  render() {
    const { children, ...rest } = this.props;
    delete rest.instance;
    delete rest.meta;
    delete rest.global;

    const { onMouseLeave, onMouseEnter } = this.handlers;

    const elementType = process.env.isMiniprogram ? 'wx-button' : 'div';
    // wx-button 视为 Web Components, 故使用 class, 见 https://reactjs.org/docs/dom-elements.html#classname
    const classKey = process.env.isMiniprogram ? 'class' : 'className';

    return React.createElement(
      elementType,
      {
        [classKey]: '__impage-component-actions-proxy',
        id: getComponentId(this.props.instance.key),
        style: this.props.instance.wrapperStyle,
        ref: this.setNode,
        'data-key': this.props.instance.key,
        onClick: this.onSingleClick,
        onDoubleClick: this.onDoubleClick,
        onMouseEnter,
        onMouseLeave,
        onTouchStart: this.onComponentPress,
        onTouchEnd: this.onComponentPressRelease,
        onMouseDown: this.onComponentPress,
        onMouseUp: this.onComponentPressRelease,
      },
      R.isEmpty(rest) ? children : React.Children.map(children, (child) => React.cloneElement(child, rest)),
    );
  }
}
